import React, { useEffect, useState } from 'react';
import { SingleValue, MultiValue } from 'react-select';
import SimpleSelect from '../Select/SimpleSelect';
import { useEmployeeSelection } from '../../providers/EmployeeSelectionProvider';
import { fetchEmployeesByRole } from '../../api/team';
import { UserNameIdDto } from '../../api/api-types';
import styles from './employee-selection.module.scss';
import { roleCheckerOtherEmployee } from '../../utils/roleCheckerOtherEmployee';
import { Msg } from '../../store/actions';
import { useTranslation } from 'react-i18next';
import { timeTrackingLongStyle } from '../Select/selectStyles';
import ScreenBlurOverlay from '../ScreenBlurOverlay/ScreenBlurOverlay';
import classNames from 'classnames';
import { useAppDispatch } from '../../hooks/redux';
import { formatDateYYYYMMDD } from '../../utils/date-formatting';
import { createGetTimeLogsList } from '../../pages/TimeTrackingPage/utils/fetchTimeLogs';

interface EmployeeSelectionDropdownProps {
  onEmployeeChange?: (employeeId: number | null) => void;
  setHandleValues?: React.Dispatch<Msg>;
}

type EmployeeOption = {
  id: number;
  label: string;
  value: string;
};

export const EmployeeSelectionDropdown: React.FC<
  EmployeeSelectionDropdownProps
> = ({ onEmployeeChange, setHandleValues }) => {
  const { selectedEmployee, setSelectedEmployee, clearSelectedEmployee } =
    useEmployeeSelection();
  const [employees, setEmployees] = useState<UserNameIdDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const dispatch = useAppDispatch();
  const isManager = roleCheckerOtherEmployee();

  useEffect(() => {
    if (!isManager) return;

    const loadEmployees = async () => {
      setIsLoading(true);
      try {
        const employeeData = await fetchEmployeesByRole();
        if (employeeData) {
          setEmployees(employeeData);
        }
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployees();
  }, [isManager]);

  const handleEmployeeSelect = (
    newValue: MultiValue<EmployeeOption> | SingleValue<EmployeeOption>,
  ) => {
    if (Array.isArray(newValue) || !newValue) return;

    setIsVisible(true);

    const selectedOption = newValue as EmployeeOption;
    const employee = employees.find((emp) => emp.id === selectedOption.id);
    if (employee) {
      setTimeout(() => {
        setSelectedEmployee(employee);
        onEmployeeChange?.(employee.id || null);
        setIsVisible(false);
      }, 500);
    }
  };

  const handleClearSelection = () => {
    setIsVisible(true);
    const getTimeLogsList = createGetTimeLogsList({
      currentDate: formatDateYYYYMMDD(new Date()),
      dispatch,
      setHandleValues: setHandleValues as React.Dispatch<Msg>,
      userId: selectedEmployee?.id || 0,
    });
    getTimeLogsList();
    setTimeout(() => {
      clearSelectedEmployee();
      onEmployeeChange?.(null);
      setIsVisible(false);
    }, 500);
  };

  if (!isManager) {
    return null;
  }

  const employeeOptions = employees.map((employee) => ({
    id: employee.id || 0,
    label: employee.name || '',
    value: employee.name || '',
  }));

  const selectedValue = selectedEmployee
    ? {
        id: selectedEmployee.id || 0,
        label: selectedEmployee.name || '',
        value: selectedEmployee.name || '',
      }
    : {
        id: 0,
        label: 'Select employee...',
        value: '',
      };

  return (
    <div className={styles.headerContainer}>
      <button
        className={classNames(styles.resetButton, {
          [styles.visible]: selectedEmployee,
          [styles.hidden]: !selectedEmployee,
        })}
        onClick={handleClearSelection}
      >
        {t('sickness.switchToMyView')}
      </button>

      <div className={styles.headerDropdown}>
        <SimpleSelect
          selectWrapperClass='select_vertical_wrapper'
          selectTitleClassName='select_title'
          options={employeeOptions}
          value={selectedValue}
          onChange={handleEmployeeSelect}
          isLoading={isLoading}
          placeholder={t('sickness.selectEmployee')}
          styles={timeTrackingLongStyle}
        />
      </div>
      <ScreenBlurOverlay isVisible={isVisible} />
    </div>
  );
};
